// ═══════════════════════════════════════════════════════════
// Incident Store — CRUD + activity log with server-side query
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchRecords, queryRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { useAuthStore, type Incident } from '../auth/auth-store';

const F = FIELDS.incidents;
const FA = FIELDS.activityLog;

// Map V2 flat record to typed Incident
function mapIncident(row: Record<string, unknown>): Incident {
  const incType = (row[F.incidentType] as string) || '';
  return {
    id: row.record_id as number,
    name: (row[F.name] as string) || '',
    type: incType,
    incidentType: incType,
    status: (row[F.status] as string) || 'active',
    location: (row[F.location] as string) || '',
    latitude: String(row[F.latitude] || ''),
    longitude: String(row[F.longitude] || ''),
    description: (row[F.description] as string) || '',
    commander: (row[F.commander] as string) || '',
    startDate: (row[F.startDate] as string) || '',
    endDate: (row[F.endDate] as string) || '',
    severity: (row[F.severity] as string) || '',
    createdByName: (row[F.createdByName] as string) || '',
    createdByEmail: (row[F.createdByEmail] as string) || '',
    datecreated: (row.created_at as string) || '',
    state: (row[F.state] as string) || '',
    county: (row[F.county] as string) || '',
    city: (row[F.city] as string) || '',
  };
}

// Convert any date string to a full UTC ISO-8601 timestamp that GAB/Postgres accepts.
// Sending bare "YYYY-MM-DD" causes GAB's server to append a local tz offset like
// "-04:00", which Postgres rejects as invalid. Sending "YYYY-MM-DDT00:00:00.000Z" is
// unambiguous and always accepted.
function toDateOnly(val: string | undefined): string {
  if (!val) return '';
  const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? `${m[1]}T00:00:00.000Z` : '';
}

// Build field payload for create/update
function toPayload(data: Partial<Incident>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.name !== undefined) p[F.name] = data.name;
  if (data.type !== undefined) p[F.incidentType] = data.type;
  if (data.incidentType !== undefined) p[F.incidentType] = data.incidentType;
  if (data.status !== undefined) p[F.status] = data.status;
  if (data.location !== undefined) p[F.location] = data.location;
  if (data.latitude !== undefined) p[F.latitude] = parseFloat(data.latitude) || null;
  if (data.longitude !== undefined) p[F.longitude] = parseFloat(data.longitude) || null;
  if (data.description !== undefined) p[F.description] = data.description;
  if (data.commander !== undefined) p[F.commander] = data.commander;
  if (data.startDate !== undefined) p[F.startDate] = toDateOnly(data.startDate);
  if (data.endDate !== undefined) p[F.endDate] = toDateOnly(data.endDate);
  if (data.severity !== undefined) p[F.severity] = data.severity;
  if (data.state !== undefined) p[F.state] = data.state;
  if (data.county !== undefined) p[F.county] = data.county;
  if (data.city !== undefined) p[F.city] = data.city;
  return p;
}

export interface ActivityLogEntry {
  id: number;
  incidentId: string;
  module: string;
  action: string;
  actorName: string;
  actorEmail: string;
  targetType: string;
  targetId: string;
  details: string;
  datecreated: string;
}

function mapActivity(row: Record<string, unknown>): ActivityLogEntry {
  return {
    id: row.record_id as number,
    incidentId: String(row[FA.incidentId] || ''),
    module: (row[FA.module] as string) || '',
    action: (row[FA.action] as string) || '',
    actorName: (row[FA.actorName] as string) || '',
    actorEmail: (row[FA.actorEmail] as string) || '',
    targetType: (row[FA.targetType] as string) || '',
    targetId: (row[FA.targetId] as string) || '',
    details: (row[FA.details] as string) || '',
    datecreated: (row.created_at as string) || '',
  };
}

interface IncidentStoreState {
  incidents: Incident[];
  total: number;
  loading: boolean;
  error: string | null;
  activityLog: ActivityLogEntry[];

  fetch: () => Promise<void>;
  create: (data: Partial<Incident>) => Promise<Incident | null>;
  update: (id: number, data: Partial<Incident>) => Promise<void>;
  remove: (id: number) => Promise<void>;
  loadActivityLog: (incidentId: string) => Promise<void>;
  // V1 compat aliases
  isLoading: boolean;
  loadIncidents: () => Promise<void>;
  createIncident: (data: Partial<Incident>) => Promise<Incident | null>;
  updateIncident: (id: number, data: Partial<Incident>) => Promise<void>;
  deleteIncident: (id: number) => Promise<void>;
}

export const useIncidentStore = create<IncidentStoreState>((set, get) => ({
  incidents: [],
  total: 0,
  loading: false,
  error: null,
  activityLog: [],

  fetch: async () => {
    if (!TABLES.incidents) return;
    set({ loading: true, error: null });
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.incidents, {
        limit: 200,
        sort: { field: 'created_at', direction: 'desc' },
      });
      const incidents = res.records.map(mapIncident);
      set({ incidents, total: res.total });
      // Sync with auth store
      useAuthStore.getState().setIncidents(incidents);
    } catch (e) {
      console.error('[IncidentStore] fetch', e);
      set({ error: (e as Error).message });
    }
    set({ loading: false });
  },

  create: async (data) => {
    if (!TABLES.incidents) return null;
    set({ loading: true });
    try {
      const user = useAuthStore.getState().user;
      const payload = toPayload(data);
      if (F.createdByName) payload[F.createdByName] = user?.fullName || '';
      if (F.createdByEmail) payload[F.createdByEmail] = user?.email || '';
      const res = await createRecord<Record<string, unknown>>(TABLES.incidents, payload);
      await get().fetch();
      return mapIncident(res);
    } catch (e) {
      console.error('[IncidentStore] create', e);
      return null;
    } finally {
      set({ loading: false });
    }
  },

  update: async (id, data) => {
    if (!TABLES.incidents) return;
    set({ loading: true });
    try {
      await updateRecord(TABLES.incidents, id, toPayload(data));
      await get().fetch();
    } catch (e) {
      console.error('[IncidentStore] update', e);
    } finally {
      set({ loading: false });
    }
  },

  remove: async (id) => {
    if (!TABLES.incidents) return;
    set({ loading: true });
    try {
      await deleteRecord(TABLES.incidents, id);
      await get().fetch();
      const active = useAuthStore.getState().activeIncident;
      if (active?.id === id) useAuthStore.getState().setActiveIncident(null);
    } catch (e) {
      console.error('[IncidentStore] remove', e);
    } finally {
      set({ loading: false });
    }
  },

  loadActivityLog: async (incidentId: string) => {
    if (!TABLES.activityLog) return;
    try {
      const res = await queryRecords<Record<string, unknown>>(TABLES.activityLog, {
        limit: 100,
        sort: { field: 'created_at', direction: 'desc' },
        filters: [{ field: FA.incidentId, operator: 'eq', value: incidentId }],
      });
      set({ activityLog: res.records.map(mapActivity) });
    } catch (e) {
      console.error('[IncidentStore] loadActivityLog', e);
    }
  },

  // V1 compat aliases
  isLoading: false,
  loadIncidents: async () => { await get().fetch(); },
  createIncident: async (data) => { return get().create(data); },
  updateIncident: async (id, data) => { return get().update(id, data); },
  deleteIncident: async (id) => { return get().remove(id); },
}));
